/**
 * `AddonEditView` — single add-on editor (ADR-0263 §2.5).
 *
 * Two modes (selected by `addonId`):
 *   - `addonId === null`     → create. POST then PUT prices.
 *   - `addonId === '<id>'`   → edit. GET on mount, PATCH + PUT prices on save.
 *
 * Kind + scope are CREATE-ONLY (the server rejects a PATCH that
 * carries either with HTTP 400 — existing purchases are bound to the
 * kind/scope they were placed under). The form hides those fields
 * once the add-on exists.
 *
 * All fields are inert (S1): nothing bills, nothing enforces.
 */

import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

import type {
  AdminPlanAddonDetailResponse,
  AdminPlanAddonKind,
  AdminPlanAddonStatus,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { Field } from '../../components/atoms/Field.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact } from '../../lib/format.js';
import { InertBanner } from './InertBanner.js';
import {
  ALL_ADDON_KINDS,
  KIND_SCOPE,
  addonKindLabel,
  addonUnitHint,
} from './addon-helpers.js';
import { findPrice, planStatusTone, tenXMonthly } from './plan-helpers.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

const ADDON_STATUS_OPTIONS: readonly AdminPlanAddonStatus[] = [
  'draft',
  'active',
  'archived',
];

interface FormState {
  name: string;
  slug: string;
  description: string;
  kind: AdminPlanAddonKind;
  unit_value: string;
  status: AdminPlanAddonStatus;
  monthly_amount: string;
  monthly_currency: string;
  yearly_amount: string;
  yearly_currency: string;
}

function blankForm(): FormState {
  return {
    name: '',
    slug: '',
    description: '',
    kind: 'page_loads_pack',
    unit_value: '',
    status: 'draft',
    monthly_amount: '',
    monthly_currency: 'usd',
    yearly_amount: '',
    yearly_currency: 'usd',
  };
}

function detailToForm(detail: AdminPlanAddonDetailResponse): FormState {
  const monthly = findPrice(detail.prices, 'monthly');
  const yearly = findPrice(detail.prices, 'yearly');
  return {
    name: detail.addon.name,
    slug: detail.addon.slug,
    description: detail.addon.description ?? '',
    kind: detail.addon.kind,
    unit_value: String(detail.addon.unit_value),
    status: detail.addon.status,
    monthly_amount: monthly === undefined ? '' : String(monthly.amount_minor),
    monthly_currency: monthly?.currency ?? 'usd',
    yearly_amount: yearly === undefined ? '' : String(yearly.amount_minor),
    yearly_currency: yearly?.currency ?? 'usd',
  };
}

export interface AddonEditViewProps {
  setView: SetView;
  /** null = create mode; string = edit mode. */
  addonId: string | null;
}

export function AddonEditView({ setView, addonId }: AddonEditViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const isCreate = addonId === null;

  const [detail, setDetail] = useState<AdminPlanAddonDetailResponse | null>(null);
  const [form, setForm] = useState<FormState>(() => blankForm());
  const [loading, setLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successAt, setSuccessAt] = useState<number | null>(null);

  useEffect(() => {
    if (isCreate) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getAddon(addonId);
        if (cancelled) return;
        setDetail(res);
        setForm(detailToForm(res));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        if (err instanceof ApiError && err.kind === 'not_found') {
          setLoadError('Add-on not found.');
          return;
        }
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setLoadError(`Could not load add-on${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isCreate, addonId]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((cur) => ({ ...cur, [key]: value }));
  };

  const validate = (): string | null => {
    if (form.name.trim().length === 0) return 'Add-on name is required.';
    if (form.slug.trim().length === 0) return 'Slug is required.';
    const uv = Number(form.unit_value.trim());
    if (!Number.isFinite(uv) || !Number.isInteger(uv) || uv < 1) {
      return 'Unit value must be a positive whole number.';
    }
    for (const [interval, amount] of [
      ['monthly', form.monthly_amount],
      ['yearly', form.yearly_amount],
    ] as const) {
      const trimmed = amount.trim();
      if (trimmed.length === 0) continue;
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        return `${interval} amount must be a non-negative whole number in minor units.`;
      }
    }
    return null;
  };

  const doSave = async (): Promise<void> => {
    if (saving) return;
    const v = validate();
    if (v !== null) {
      setSaveError(v);
      return;
    }
    setSaving(true);
    setSaveError(null);

    try {
      let effectiveAddonId: string;

      if (isCreate) {
        const created = await api.createAddon({
          name: form.name.trim(),
          slug: form.slug.trim(),
          description:
            form.description.trim().length === 0 ? null : form.description.trim(),
          kind: form.kind,
          scope: KIND_SCOPE[form.kind],
          unit_value: Number(form.unit_value.trim()),
        });
        effectiveAddonId = created.addon.addon_id;
        if (form.status !== 'draft') {
          await api.updateAddon(effectiveAddonId, { status: form.status });
        }
      } else {
        effectiveAddonId = addonId;
        const cur = detail?.addon;
        const draft: {
          name?: string;
          description?: string | null;
          status?: AdminPlanAddonStatus;
          unit_value?: number;
        } = {};
        if (cur !== undefined) {
          if (form.name.trim() !== cur.name) draft.name = form.name.trim();
          const nextDesc =
            form.description.trim().length === 0 ? null : form.description.trim();
          if (nextDesc !== (cur.description ?? null)) draft.description = nextDesc;
          if (form.status !== cur.status) draft.status = form.status;
          const uv = Number(form.unit_value.trim());
          if (uv !== cur.unit_value) draft.unit_value = uv;
        }
        if (Object.keys(draft).length > 0) {
          await api.updateAddon(effectiveAddonId, draft);
        }
      }

      const prices: Array<{
        interval: 'monthly' | 'yearly';
        amount_minor: number;
        currency: string;
      }> = [];
      if (form.monthly_amount.trim().length > 0) {
        prices.push({
          interval: 'monthly',
          amount_minor: Number(form.monthly_amount.trim()),
          currency: form.monthly_currency.trim().toLowerCase(),
        });
      }
      if (form.yearly_amount.trim().length > 0) {
        prices.push({
          interval: 'yearly',
          amount_minor: Number(form.yearly_amount.trim()),
          currency: form.yearly_currency.trim().toLowerCase(),
        });
      }
      await api.putAddonPrices(effectiveAddonId, { prices });

      const fresh = await api.getAddon(effectiveAddonId);
      setDetail(fresh);
      setForm(detailToForm(fresh));
      setSuccessAt(Date.now());

      if (isCreate) {
        setView('addon-detail', { addonId: effectiveAddonId });
      }
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not save add-on${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <button
        type="button"
        onClick={() => setView('plans')}
        className="flex items-center gap-1 mb-4"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          background: 'transparent',
          border: 0,
          padding: 0,
          minHeight: 40,
        }}
      >
        <ArrowLeft size={12} /> Plan catalog
      </button>

      {!isCreate && loading ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      ) : loadError !== null ? (
        <Card style={{ padding: 16 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Load failed
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
            {loadError}
          </div>
        </Card>
      ) : (
        <>
          <AddonHeader
            isCreate={isCreate}
            name={form.name}
            status={form.status}
            addonId={detail?.addon.addon_id ?? null}
            updatedAt={detail?.addon.updated_at ?? null}
          />

          <InertBanner flavor="v2" />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void doSave();
            }}
            className="grid grid-cols-1 gap-4"
          >
            <Card style={{ padding: 20 }}>
              <SectionKicker>Identity</SectionKicker>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    className="w-full px-3 py-2 outline-none"
                    style={inputStyle(C)}
                    placeholder="Page-loads pack · 10k"
                  />
                </Field>
                <Field
                  label="Slug"
                  hint="URL-safe identifier (lowercase, dashes)."
                  required
                >
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => update('slug', e.target.value)}
                    disabled={!isCreate}
                    className="w-full px-3 py-2 outline-none"
                    style={{
                      ...inputStyle(C),
                      fontFamily: monoFont,
                      opacity: isCreate ? 1 : 0.6,
                    }}
                    placeholder="page-loads-10k"
                  />
                </Field>
                <Field
                  label="Description"
                  hint="Internal — shown to staff, not to merchants."
                >
                  <textarea
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 outline-none sm:col-span-2"
                    style={{ ...inputStyle(C), resize: 'vertical' }}
                  />
                </Field>
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(e) =>
                      update('status', e.target.value as AdminPlanAddonStatus)
                    }
                    className="w-full px-3 py-2 outline-none"
                    style={inputStyle(C)}
                  >
                    {ADDON_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </Card>

            <Card style={{ padding: 20 }}>
              <SectionKicker>Kind + unit</SectionKicker>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Kind"
                  hint={
                    isCreate
                      ? 'Scope follows the kind: Extra Free site = account; packs = site.'
                      : 'Kind is set at create and cannot change.'
                  }
                  required
                >
                  <select
                    value={form.kind}
                    onChange={(e) =>
                      update('kind', e.target.value as AdminPlanAddonKind)
                    }
                    disabled={!isCreate}
                    className="w-full px-3 py-2 outline-none"
                    style={{ ...inputStyle(C), opacity: isCreate ? 1 : 0.6 }}
                  >
                    {ALL_ADDON_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {addonKindLabel(k)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Unit value"
                  hint={`One pack adds this many ${addonUnitHint(form.kind)}.`}
                  required
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.unit_value}
                    onChange={(e) => update('unit_value', e.target.value)}
                    className="w-full px-3 py-2 outline-none"
                    style={{ ...inputStyle(C), fontFamily: monoFont }}
                    placeholder={
                      form.kind === 'extra_free_site' ? '1' : '10000'
                    }
                  />
                </Field>
              </div>
            </Card>

            <Card style={{ padding: 20 }}>
              <SectionKicker>Pricing</SectionKicker>
              <div className="grid sm:grid-cols-2 gap-4">
                <PriceFieldset
                  label="Monthly"
                  amount={form.monthly_amount}
                  currency={form.monthly_currency}
                  onAmount={(v) => update('monthly_amount', v)}
                  onCurrency={(v) => update('monthly_currency', v)}
                />
                <YearlyPriceFieldset
                  label="Yearly"
                  amount={form.yearly_amount}
                  currency={form.yearly_currency}
                  monthlyAmount={form.monthly_amount}
                  onAmount={(v) => update('yearly_amount', v)}
                  onCurrency={(v) => update('yearly_currency', v)}
                />
              </div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 11,
                  color: C.muted,
                  marginTop: 8,
                  lineHeight: 1.5,
                }}
              >
                Amounts are in the currency&rsquo;s minor unit (USD cents).
                Leave an interval blank to omit it. Saving REPLACES the add-on&rsquo;s
                price set.
              </div>
            </Card>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
              <div className="flex items-center gap-3 flex-wrap">
                {saveError !== null ? (
                  <span
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.coralFg,
                    }}
                  >
                    {saveError}
                  </span>
                ) : successAt !== null && Date.now() - successAt < 4000 ? (
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.limeFg,
                      letterSpacing: 2,
                      textTransform: 'uppercase',
                    }}
                  >
                    Saved
                  </span>
                ) : null}
              </div>
              <button
                type="submit"
                disabled={saving}
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '10px 16px',
                  borderRadius: 2,
                  border: 0,
                  opacity: saving ? 0.6 : 1,
                  minHeight: 44,
                }}
              >
                {saving ? 'Saving…' : isCreate ? 'Create add-on' : 'Save add-on'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

function AddonHeader({
  isCreate,
  name,
  status,
  addonId,
  updatedAt,
}: {
  isCreate: boolean;
  name: string;
  status: AdminPlanAddonStatus;
  addonId: string | null;
  updatedAt: string | null;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div className="min-w-0">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 10,
            wordBreak: 'break-all',
          }}
        >
          {isCreate ? 'New add-on' : `Add-on · ${addonId ?? ''}`}
        </div>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(28px, 6vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
            margin: 0,
            wordBreak: 'break-word',
          }}
        >
          {name.trim().length === 0 ? 'Untitled add-on' : name}
        </h1>
        {updatedAt !== null ? (
          <div
            style={{
              marginTop: 10,
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
            }}
          >
            Updated {formatIsoCompact(updatedAt)}
          </div>
        ) : null}
      </div>
      {!isCreate ? (
        <Pill tone={planStatusTone(status)}>{status}</Pill>
      ) : null}
    </div>
  );
}

function SectionKicker({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function PriceFieldset({
  label,
  amount,
  currency,
  onAmount,
  onCurrency,
}: {
  label: string;
  amount: string;
  currency: string;
  onAmount: (v: string) => void;
  onCurrency: (v: string) => void;
}): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: 12,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          className="flex-1 px-3 py-2 outline-none"
          style={{ ...inputStyle(C), fontFamily: monoFont }}
          placeholder="0"
          aria-label={`${label} amount in minor units`}
        />
        <input
          type="text"
          value={currency}
          onChange={(e) => onCurrency(e.target.value.toLowerCase())}
          maxLength={3}
          className="px-3 py-2 outline-none uppercase"
          style={{
            ...inputStyle(C),
            fontFamily: monoFont,
            width: 88,
            textAlign: 'center',
          }}
          placeholder="USD"
          aria-label={`${label} currency`}
        />
      </div>
    </div>
  );
}

function YearlyPriceFieldset(props: {
  label: string;
  amount: string;
  currency: string;
  monthlyAmount: string;
  onAmount: (v: string) => void;
  onCurrency: (v: string) => void;
}): JSX.Element {
  const C = useC();
  const canFill = tenXMonthly(props.monthlyAmount).length > 0;
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: 12,
      }}
    >
      <div
        className="flex items-center justify-between gap-2"
        style={{ marginBottom: 8 }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          {props.label}
        </div>
        <button
          type="button"
          onClick={() => props.onAmount(tenXMonthly(props.monthlyAmount))}
          disabled={!canFill}
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: canFill ? C.text : C.dim,
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '4px 8px',
            cursor: canFill ? 'pointer' : 'not-allowed',
          }}
          title="Set yearly to monthly × 10 (two months free)"
        >
          10 × monthly (two months free)
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={props.amount}
          onChange={(e) => props.onAmount(e.target.value)}
          className="flex-1 px-3 py-2 outline-none"
          style={{ ...inputStyle(C), fontFamily: monoFont }}
          placeholder="0"
          aria-label={`${props.label} amount in minor units`}
        />
        <input
          type="text"
          value={props.currency}
          onChange={(e) => props.onCurrency(e.target.value.toLowerCase())}
          maxLength={3}
          className="px-3 py-2 outline-none uppercase"
          style={{
            ...inputStyle(C),
            fontFamily: monoFont,
            width: 88,
            textAlign: 'center',
          }}
          placeholder="USD"
          aria-label={`${props.label} currency`}
        />
      </div>
    </div>
  );
}

function inputStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 13,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
  };
}
